import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Roles } from '@/enums/roles.enum';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Menu,
  User,
  Library,
  Users,
  LogOut,
  ChevronRight,
  Bug
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MobileHamburgerMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const handleNavigation = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="flex flex-col items-center justify-center min-w-0 flex-1 py-2 px-1 relative transition-colors duration-200 text-muted-foreground hover:text-foreground">
          <Menu className="h-5 w-5 mb-1" />
          <span className="text-xs font-medium truncate max-w-full">
            More
          </span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[350px]">
        <SheetHeader>
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 space-y-1">
          {/* User Profile Section */}
          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="bg-primary rounded-full w-10 h-10 flex items-center justify-center text-primary-foreground font-medium text-sm flex-shrink-0">
                {user?.firstName?.charAt(0).toUpperCase()}{user?.lastName?.charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium text-sm truncate">
                  {`${user?.firstName} ${user?.lastName}`.trim()}
                </span>
                <span className="text-xs text-muted-foreground capitalize truncate">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Navigation Items */}
          <div className="space-y-1">
            <button
              onClick={() => handleNavigation('/settings')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive('/settings')
                  ? "bg-primary/10 text-primary"
                  : "text-foreground hover:bg-muted"
              )}
            >
              <div className="flex items-center gap-3">
                <User className="h-4 w-4" />
                <span>Profile & Settings</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>

            {user?.role === Roles.ADMIN && (
              <>
                <button
                  onClick={() => handleNavigation('/resources')}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                    isActive('/resources')
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Library className="h-4 w-4" />
                    <span>Company Resources</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>

                <button
                  onClick={() => handleNavigation('/users')}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                    isActive('/users')
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4" />
                    <span>User Management</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </>
            )}

            <button
              onClick={() => handleNavigation('/report-bug')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive('/report-bug')
                  ? "bg-primary/10 text-primary"
                  : "text-foreground hover:bg-muted"
              )}
            >
              <div className="flex items-center gap-3">
                <Bug className="h-4 w-4" />
                <span>Report a Bug</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          <Separator className="my-4" />

          {/* Logout Button */}
          <Button
            variant="outline"
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileHamburgerMenu;
